import { lazy } from "react";

const RELOADED = "msbs-reloaded-for-screen";

/**
 * React.lazy, but a screen that fails to load reloads the page once.
 *
 * Each screen is a separate file fetched on first visit. When that fetch
 * fails, nothing catches it and the page stays white. It fails during
 * maintenance (nginx answers 503 for every file) and after a new release (the
 * old file name no longer exists). A full reload then shows the maintenance
 * page, or loads the new release.
 *
 * Once only: should the reload fail the same way, the error is thrown as
 * before rather than reloading in a loop. A screen that loads clears the mark.
 *
 * The last two arguments exist so a test can hand in its own window and
 * storage. Callers pass only `load`.
 */
export function loadWithReload(load, win = window, store = window.sessionStorage) {
  return load().then(
    (module) => {
      try {
        store.removeItem(RELOADED);
      } catch (error) {
        // Unavailable storage only costs the loop guard.
      }
      return module;
    },
    (error) => {
      let reloadedBefore = false;
      try {
        reloadedBefore = store.getItem(RELOADED) !== null;
        if (!reloadedBefore) store.setItem(RELOADED, "1");
      } catch (storageError) {
        reloadedBefore = true;
      }
      if (reloadedBefore) throw error;
      win.location.reload();
      return new Promise(() => {});
    }
  );
}

export default function lazyWithReload(load) {
  return lazy(() => loadWithReload(load));
}
